import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { clearAuthSession } from "../auth/authSession";

const rawBaseQuery = fetchBaseQuery({
  baseUrl:
    process.env.REACT_APP_API_BASE_URL ||
    "https://nodebackend.steamminds.org/api/v1",
  credentials: "include",
});

const AUTH_ENDPOINTS = ["/auth/login", "/auth/logout"];

let isRedirectingToLogin = false;

const getRequestUrl = (args) => {
  if (typeof args === "string") {
    return args;
  }

  return args?.url || "";
};

const isAuthEndpoint = (url) =>
  AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));

const getErrorMessage = (error) =>
  String(
    error?.data?.message ||
      error?.data?.error ||
      error?.error ||
      error?.message ||
      "",
  ).toLowerCase();

const isSessionFailure = (error) => {
  if (!error) {
    return false;
  }

  const message = getErrorMessage(error);
  const sessionEnded =
    message.includes("jwt") ||
    message.includes("token is required") ||
    message.includes("token not provided") ||
    message.includes("invalid token") ||
    message.includes("token expired") ||
    message.includes("no token") ||
    message.includes("not authenticated") ||
    message.includes("please log in") ||
    message.includes("please login") ||
    message.includes("session expired");

  // 403 is a permission denial for a signed-in user. Logging out here sends
  // every non-admin role back to the login page as soon as a page loads.
  if (sessionEnded) {
    return true;
  }

  return error.status === 401;
};

const redirectToLogin = () => {
  if (isRedirectingToLogin) {
    return;
  }

  isRedirectingToLogin = true;
  clearAuthSession();

  if (window.location.pathname !== "/") {
    window.location.replace("/");
    return;
  }

  isRedirectingToLogin = false;
};

const baseQueryWithCredentials = async (args, api, extraOptions) => {
  const request =
    typeof args === "string"
      ? { url: args, credentials: "include" }
      : { ...args, credentials: args?.credentials || "include" };

  const result = await rawBaseQuery(request, api, extraOptions);
  const url = getRequestUrl(args);

  if (
    !extraOptions?.skipAuthRedirect &&
    !isAuthEndpoint(url) &&
    isSessionFailure(result.error)
  ) {
    api.dispatch(baseApi.util.resetApiState());
    redirectToLogin();
  }

  return result;
};

const baseApi = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithCredentials,
  tagTypes: [
    "Enrollments",
    "Categories",
    "SubCategories",
    "AgeGroups",
    "Levels",
    "PdfMaterials",
    "Lectures",
    "AssignedInstructors",
    "StaffWithRoles",
    "StaffPendingRoles",
    "RolesList",
    "StudentProgress",
    "Instructors",
    "CourseCatalog",
    "CourseCompletion",
    "CourseSessions",
    "Certificates",
  ],
  endpoints: () => ({}),
});

export default baseApi;
